import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import CancelIcon from "@mui/icons-material/Cancel";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DEGREE_OPTIONS } from "config";
import { useCvStepNavigation } from "../../../contexts/CvStepNavigationContext";
import { usePatchCvMutation } from "../../../lib/cvsSlice";

// ── Date helpers ──────────────────────────────────────────────────────────────

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// "2020-06" → { month: "June", year: "2020" }
function fromMonthInput(value) {
  if (!value) return { month: "", year: "" };
  const [year, m] = value.split("-");
  return { month: MONTHS[parseInt(m, 10) - 1] ?? "", year };
}

// "June", "2020" → "2020-06"
function toMonthInput(monthName, year) {
  if (!year) return "";
  const idx = MONTHS.indexOf(monthName);
  const m = idx >= 0 ? String(idx + 1).padStart(2, "0") : "01";
  return `${year}-${m}`;
}

// "June 2020 - December 2023"
function formatDateRange(startMonth, startYear, endMonth, endYear) {
  const start = [startMonth, startYear].filter(Boolean).join(" ");
  const end = [endMonth, endYear].filter(Boolean).join(" ");
  return [start, end].filter(Boolean).join(" - ");
}

// ── Constants ─────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  achievement: "",
  institute: "",
  startDate: "",    // native month input: "YYYY-MM"
  endDate: "",
  fieldOfStudy: "", // not shown in UI; carried through to avoid wiping existing data on edit
};

// ── Shared form (inline + modal) ──────────────────────────────────────────────

const EducationForm = ({ form, setField }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <TextField
      select
      label="Degree"
      value={form.achievement}
      onChange={(e) => setField("achievement", e.target.value)}
      fullWidth
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
    >
      {DEGREE_OPTIONS.map((deg) => (
        <MenuItem key={deg} value={deg}>
          {deg}
        </MenuItem>
      ))}
    </TextField>

    <TextField
      label="Issuing Institute"
      placeholder="Enter Name"
      value={form.institute}
      onChange={(e) => setField("institute", e.target.value)}
      fullWidth
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
    />

    <TextField
      label="Start Date"
      type="month"
      value={form.startDate}
      onChange={(e) => setField("startDate", e.target.value)}
      fullWidth
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
    />

    <TextField
      label="End Date"
      type="month"
      value={form.endDate}
      onChange={(e) => setField("endDate", e.target.value)}
      fullWidth
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
      inputProps={{ min: form.startDate }}
    />
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────

const Education = ({ educations, cvId }) => {
  const [localEducations, setLocalEducations] = useState(
    Array.isArray(educations) ? educations : []
  );
  const [form, setForm] = useState(EMPTY_FORM);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalForm, setModalForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalSaving, setIsModalSaving] = useState(false);
  const isDirty = useRef(false);

  const [patchCv] = usePatchCvMutation();
  const { goToNextStep, goToPrevStep } = useCvStepNavigation();

  // When the CV itself changes, always re-sync and clear the dirty flag
  useEffect(() => {
    isDirty.current = false;
    setLocalEducations(Array.isArray(educations) ? educations : []);
  }, [cvId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guard against the 1-second CvDetails poll resetting state while the user edits
  useEffect(() => {
    if (isDirty.current) return;
    setLocalEducations(Array.isArray(educations) ? educations : []);
  }, [educations]);

  const setField = (key, value) => {
    isDirty.current = true;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setModalField = (key, value) => {
    isDirty.current = true;
    setModalForm((prev) => ({ ...prev, [key]: value }));
  };

  const buildPayload = (f) => {
    const { month: startMonth, year: startYear } = fromMonthInput(f.startDate);
    const { month: endMonth, year: endYear } = fromMonthInput(f.endDate);
    return {
      achievement: f.achievement,
      institute: f.institute.trim(),
      fieldOfStudy: f.fieldOfStudy ?? "", // carried from existing record on edit; "" for new
      startMonth,
      startYear,
      endMonth,
      endYear,
    };
  };

  const validate = (f) => {
    if (!f.achievement)          { toast.error("Degree is required");                return false; }
    if (!f.institute.trim())     { toast.error("Issuing institute is required");     return false; }
    if (!f.startDate)            { toast.error("Start date is required");            return false; }
    if (!f.endDate)              { toast.error("End date is required");              return false; }
    if (f.endDate < f.startDate) { toast.error("End date must be after start date"); return false; }
    return true;
  };

  // ── Inline save (State A: no entries yet) ────────────────────────────────
  const handleInlineSave = async () => {
    if (!validate(form)) return;
    setIsSaving(true);
    try {
      const result = await patchCv({ cvId, educations: [buildPayload(form)] }).unwrap();
      setLocalEducations(result?.educations ?? localEducations);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(`Failed to save: ${err?.data?.message ?? err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Modal: open to add ────────────────────────────────────────────────────
  const openAddModal = () => {
    setEditingId(null);
    setModalForm(EMPTY_FORM);
    setModalOpen(true);
  };

  // ── Modal: open to edit ───────────────────────────────────────────────────
  const openEditModal = (edu) => {
    isDirty.current = true;
    setEditingId(edu._id);
    setModalForm({
      achievement: edu.achievement ?? "",
      institute: edu.institute ?? "",
      startDate: toMonthInput(edu.startMonth, edu.startYear),
      endDate: toMonthInput(edu.endMonth, edu.endYear),
      fieldOfStudy: edu.fieldOfStudy ?? "", // preserve existing value — not shown, not wiped
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalForm(EMPTY_FORM);
    setEditingId(null);
  };

  // ── Modal: save (add or update) ───────────────────────────────────────────
  const handleModalSave = async () => {
    if (!validate(modalForm)) return;
    setIsModalSaving(true);
    try {
      const payload = buildPayload(modalForm);
      const result = editingId
        ? await patchCv({ cvId, educations: [{ id: editingId, ...payload }] }).unwrap()
        : await patchCv({ cvId, educations: [payload] }).unwrap();
      setLocalEducations(result?.educations ?? localEducations);
      closeModal();
    } catch (err) {
      toast.error(`Failed to save: ${err?.data?.message ?? err.message}`);
    } finally {
      setIsModalSaving(false);
    }
  };

  // ── Modal: save & add another ─────────────────────────────────────────────
  const handleSaveAndAddAnother = async () => {
    if (!validate(modalForm)) return;
    setIsModalSaving(true);
    try {
      const payload = buildPayload(modalForm);
      const result = editingId
        ? await patchCv({ cvId, educations: [{ id: editingId, ...payload }] }).unwrap()
        : await patchCv({ cvId, educations: [payload] }).unwrap();
      setLocalEducations(result?.educations ?? localEducations);
      setModalForm(EMPTY_FORM);
      setEditingId(null);
    } catch (err) {
      toast.error(`Failed to save: ${err?.data?.message ?? err.message}`);
    } finally {
      setIsModalSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (eduId) => {
    isDirty.current = true;
    try {
      await patchCv({ cvId, educations: [{ id: eduId, delete: true }] }).unwrap();
      setLocalEducations((prev) => prev.filter((e) => e._id !== eduId));
    } catch (err) {
      toast.error(`Failed to remove: ${err?.data?.message ?? err.message}`);
    }
  };

  // ── "Include in CV" toggle ────────────────────────────────────────────────
  // checked = true  → includeInCv = true  → excludeInCv: false  (shown in CV)
  // checked = false → includeInCv = false → excludeInCv: true   (hidden from CV)
  const handleIncludeToggle = async (edu, includeInCv) => {
    isDirty.current = true;
    const excludeInCv = !includeInCv;
    setLocalEducations((prev) =>
      prev.map((e) => (e._id === edu._id ? { ...e, excludeInCv } : e))
    );
    try {
      await patchCv({ cvId, educations: [{ id: edu._id, excludeInCv }] }).unwrap();
    } catch (err) {
      // Rollback optimistic update
      setLocalEducations((prev) =>
        prev.map((e) => (e._id === edu._id ? { ...e, excludeInCv: edu.excludeInCv } : e))
      );
      toast.error("Failed to update");
    }
  };

  const hasEducations = localEducations.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* State A: no entries — show inline form + SAVE */}
      {!hasEducations && (
        <>
          <EducationForm form={form} setField={setField} />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleInlineSave}
              disabled={isSaving}
              sx={{
                backgroundColor: "#1A1A1A",
                color: "#fff",
                "&:hover": { backgroundColor: "#3D3634" },
                "&:disabled": { backgroundColor: "#C5BFBE" },
                borderRadius: "6px",
                px: 4,
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
            >
              {isSaving ? "Saving..." : "SAVE"}
            </Button>
          </Box>
        </>
      )}

      {/* State B: entries exist — show cards + ADD ANOTHER */}
      {hasEducations && (
        <>
          {localEducations.map((edu) => (
            <Box
              key={edu._id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1.5,
                border: "1px solid #E0DEDD",
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#1A1A1A",
                    fontFamily: "Inter, sans-serif",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {edu.achievement}
                </Typography>
                <Typography
                  sx={{ fontSize: "12px", color: "#7A706F", fontFamily: "Inter, sans-serif" }}
                >
                  {formatDateRange(edu.startMonth, edu.startYear, edu.endMonth, edu.endYear)}
                </Typography>
              </Box>

              <IconButton
                size="small"
                onClick={() => openEditModal(edu)}
                sx={{ color: "#9E9E9E", "&:hover": { color: "#1A1A1A" } }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => handleDelete(edu._id)}
                sx={{ color: "#9E9E9E", "&:hover": { color: "#E84610" } }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>

              <Tooltip title="Toggle inclusion in exported CV">
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  <Switch
                    size="small"
                    checked={!edu.excludeInCv}
                    onChange={(e) => handleIncludeToggle(edu, e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#1A1A1A" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#1A1A1A",
                      },
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "11px", color: "#7A706F", whiteSpace: "nowrap" }}
                  >
                    Include in CV
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          ))}

          <Box
            component="button"
            onClick={openAddModal}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              background: "none",
              border: "none",
              cursor: "pointer",
              p: 0,
              width: "fit-content",
              color: "#1A1A1A",
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: "13px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            + ADD ANOTHER EDUCATION
          </Box>
        </>
      )}

      {/* Navigation — always visible */}
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}
      >
        <Button
          variant="outlined"
          onClick={goToPrevStep}
          sx={{
            borderColor: "#C5BFBE",
            color: "#3D3634",
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
            "&:hover": { borderColor: "#9E9E9E", backgroundColor: "#F4F2F1" },
          }}
        >
          BACK
        </Button>

        <Button
          variant="contained"
          onClick={goToNextStep}
          sx={{
            backgroundColor: "#E84610",
            "&:hover": { backgroundColor: "#C73A0D" },
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          FINISH
        </Button>
      </Box>

      {/* Add / Edit modal */}
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: "16px",
          }}
        >
          {editingId ? "Edit Education" : "Add Education"}
          <IconButton onClick={closeModal} size="small" sx={{ color: "#9E9E9E" }}>
            <CancelIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}
        >
          <EducationForm form={modalForm} setField={setModalField} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 1,
            }}
          >
            <Box
              component="button"
              onClick={handleSaveAndAddAnother}
              disabled={isModalSaving}
              sx={{
                background: "none",
                border: "none",
                cursor: "pointer",
                p: 0,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#1A1A1A",
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: "13px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                "&:hover": { textDecoration: "underline" },
                "&:disabled": { color: "#C5BFBE", cursor: "not-allowed" },
              }}
            >
              + SAVE &amp; ADD ANOTHER
            </Box>

            <Button
              variant="contained"
              onClick={handleModalSave}
              disabled={isModalSaving}
              sx={{
                backgroundColor: "#E84610",
                "&:hover": { backgroundColor: "#C73A0D" },
                "&:disabled": { backgroundColor: "#E8A090" },
                borderRadius: "6px",
                px: 4,
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
            >
              {isModalSaving ? "Saving..." : editingId ? "UPDATE" : "ADD"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Education;
